/* Fourth Man Roasters — affiliate/white-label attribution capture.
 *
 * Two ways a sale gets credited to a rep, both writing the `rep` cart attribute
 * (which rides checkout onto the order, where scripts/shopify/payout-report.mjs
 * prices commissions):
 *   1. Link:  any store URL can carry ?rep=<slug> (and/or ?partner=<name>).
 *   2. Field: a "who referred you" input on the cart (fmr-referral-field.liquid)
 *             for word-of-mouth referrals with no link click.
 * Last-touch wins.
 */
(function () {
  var KEYS = ['rep', 'partner'];

  // Rep roster — MIRROR of scripts/shopify/registry.json. Keep in sync when reps
  // are added/renamed. Aliases map what a customer might type -> the rep's slug.
  // EVERY rep gets both a first-name and a full-name alias. Customers type full
  // names — "Ryan Silver", not "ryan" — and six reps previously had no full-name
  // entry, so those entries resolved to nothing and the raw text was written as
  // the commission attribute. That is how orders ended up carrying "ryan silver "
  // and "Abraham Nam" instead of a slug.
  var REP_ALIASES = {
    andrew: 'andrew', andrei: 'andrew', andrewtalpos: 'andrew',
    cameron: 'cameron', cam: 'cameron', cameronchadwick: 'cameron',
    // marcus -> marcus-r: the slug was renamed to separate it from Marcos Sainz.
    // The bare `marcus` MUST stay — links already in circulation carry it.
    marcus: 'marcus-r', 'marcus-r': 'marcus-r', marcusrogerson: 'marcus-r',
    ryan: 'ryan', ryansilver: 'ryan',
    john: 'john-b', johnb: 'john-b', 'john-b': 'john-b', johnbruder: 'john-b',
    thies: 'thies', thiesvanrijn: 'thies',
    rob: 'rob', robert: 'rob', robcrenshaw: 'rob',
    tyler: 'tyler', tylerharmon: 'tyler',
    bodie: 'bodie', bodiewade: 'bodie',
    abe: 'abe', abewood: 'abe',
    abraham: 'abraham', abrahamnam: 'abraham',
    bao: 'bao', baokieu: 'bao', boa: 'bao', boakieu: 'bao',
    marcos: 'marcos-s', 'marcos-s': 'marcos-s', marcossainz: 'marcos-s',
    rhett: 'rhett', rhettsoliday: 'rhett',
    hank: 'hank', hankword: 'hank',
    mesacitychurch: 'mesacitychurch', mesacity: 'mesacitychurch',
    'ig-fam': 'ig-fam', igfam: 'ig-fam',
    // Vanguard University Basketball. Credited to the programme; paid to Rhett
    // (registry.json -> reps.vubball.payTo). `vanguard` alone is deliberate:
    // it is what someone types from memory, and no other rep starts with it.
    vubball: 'vubball', vanguard: 'vubball',
    vanguardbasketball: 'vubball', vubasketball: 'vubball', vanguardbball: 'vubball',
    exodus: 'exodus', exoduscoffee: 'exodus', exoduscoffeeco: 'exodus',
    // Braden Suhr. The surname alone, the first-name-plus-initial, and the
    // Brayden/Braiden spellings all credit him: an unresolved entry writes
    // rep_note instead of rep, and rep_note earns nobody anything (#1042).
    braden: 'braden', bradensuhr: 'braden', suhr: 'braden', bradens: 'braden',
    brayden: 'braden', braydensuhr: 'braden', braiden: 'braden', braidensuhr: 'braden',
    kacey: 'kacey', kaceylewis: 'kacey',
    timmy: 'timmy', timmylewis: 'timmy',
    grace: 'grace', graceflorey: 'grace',
    // Jordan and Taylor Wild, and their label Hardwood Coffee Co. (Andrew, 2026-10-19:
    // keep the slug `wild`, and let `hardwood` credit them too).
    wild: 'wild', jordanwild: 'wild', taylorwild: 'wild', taylorlilleywild: 'wild',
    hardwood: 'wild', hardwoodcoffee: 'wild', hardwoodcoffeeco: 'wild',
  };
  // Shown in the "✓ … will be credited" confirmation. First name + last initial:
  // enough for the customer to catch a wrong match, without publishing every
  // rep's full name on a public storefront.
  //
  // The disambiguation this exists for still holds: "✓ Marcus R." vs
  // "✓ Marcos S." is the pair a customer must be able to tell apart, and the
  // short forms differ in both the first name and the initial. Checked all 15
  // people for collisions before shortening; there are none. If you add a rep
  // whose short form collides with an existing one, lengthen BOTH rather than
  // leaving two identical confirmations.
  //
  // The last three are not people and are deliberately left in full: shortening
  // would produce "Mesa C.", "the T." and "Vanguard U.".
  var REP_NAMES = {
    'andrew': 'Andrew T.', 'cameron': 'Cameron C.', 'marcus-r': 'Marcus R.',
    'ryan': 'Ryan S.', 'john-b': 'John B.', 'thies': 'Thies V.',
    'rob': 'Rob C.', 'tyler': 'Tyler H.', 'bodie': 'Bodie W.',
    'abe': 'Abe W.', 'abraham': 'Abraham N.',
    'bao': 'Bao K.', 'marcos-s': 'Marcos S.', 'rhett': 'Rhett S.', 'hank': 'Hank W.',
    'mesacitychurch': 'Mesa City Church',
    'ig-fam': 'the Instagram team',
    'vubball': 'Vanguard University Basketball',
    'exodus': 'Exodus Coffee Co.',
    'braden': 'Braden S.', 'kacey': 'Kacey L.', 'timmy': 'Timmy L.', 'grace': 'Grace F.',
    'wild': 'Jordan & Taylor W.',
  };

  function resolveRep(raw) {
    if (!raw) return null;
    var s = String(raw).toLowerCase().trim();
    var hyph = s.replace(/[^a-z0-9-]/g, ''); // keep hyphens: "john-b"
    var flat = s.replace(/[^a-z0-9]/g, ''); // drop everything: "john b" -> "johnb"
    return REP_ALIASES[hyph] || REP_ALIASES[flat] || null;
  }

  function writeRep(slug) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { rep: slug } }),
    }).catch(function () {});
  }

  // Unrecognized entry: capture it verbatim on the order as `rep_note` so the team
  // can reconcile it by hand. It never auto-credits — the payout report only reads
  // `rep` — so this is safe, and it makes "we'll pass that along" actually true.
  function writeRepNote(note) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { rep_note: note } }),
    }).catch(function () {});
  }

  // A captured rep shouldn't credit a browser forever. Store the slug with a
  // timestamp and treat it as gone once it's older than REP_TTL_MS (last-touch
  // window).
  var REP_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  function storeRep(slug) {
    try {
      localStorage.setItem('fmr-rep', slug);
      localStorage.setItem('fmr-rep-ts', String(Date.now()));
    } catch (e) { /* storage unavailable — the caller does NOT depend on this */ }
  }
  function storedRep() {
    try {
      var slug = localStorage.getItem('fmr-rep');
      if (!slug) return null;
      var raw = localStorage.getItem('fmr-rep-ts');
      if (!raw) {
        // No timestamp means this rep was captured BEFORE the TTL shipped, not
        // that it expired. Treating it as expired would silently delete a valid
        // attribution from every returning customer's browser on their first
        // visit after deploy. Adopt it into the window instead.
        localStorage.setItem('fmr-rep-ts', String(Date.now()));
        return slug;
      }
      if (Date.now() - Number(raw) > REP_TTL_MS) {
        localStorage.removeItem('fmr-rep');
        localStorage.removeItem('fmr-rep-ts');
        return null;
      }
      return slug;
    } catch (e) { return null; }
  }

  // `partner` is SESSION-scoped, deliberately, and this is not symmetric with
  // `rep` on purpose.
  //
  // THE INCIDENT (#1003, 2026-07-14): partner used to live in localStorage with
  // no timestamp and no expiry, and was re-stamped on every later visit. One
  // look at a white-label site therefore branded every future order from that
  // browser — forever, with no link, no query string and an empty cart. A rep
  // testing the Maverick site in July had his own unrelated order packed and
  // shipped in a Maverick bag, and his subscription then froze that tag in, so
  // every renewal shipped the wrong brand too.
  //
  // `rep` is a 30-day last-touch credit, so persisting it across visits is the
  // whole point. `partner` is not a credit — it decides WHICH BRAND'S BAG IS
  // PHYSICALLY PACKED. A wrong one ships the wrong product, so it must only
  // last as long as the visit that actually came from that storefront. A TTL
  // would not have saved #1003: that order was six days after the visit.
  // sessionStorage survives the white-label handoff and in-store navigation
  // (which is all it needs to do) and dies with the tab.
  function storePartner(val) {
    try { sessionStorage.setItem('fmr-partner', val); } catch (e) { /* best-effort */ }
  }
  function storedPartner() {
    try { return sessionStorage.getItem('fmr-partner'); } catch (e) { return null; }
  }
  // Browsers poisoned before this shipped still carry the permanent key. Drop it
  // on sight, or those customers keep getting the wrong brand indefinitely.
  try { localStorage.removeItem('fmr-partner'); } catch (e) {}

  // ---- 1) Link-based attribution: ?rep= / ?partner= -> cart attributes -------
  try {
    var params = new URLSearchParams(window.location.search);
    var fromUrl = {}; // what THIS click carries, independent of storage

    KEYS.forEach(function (k) {
      var v = params.get(k);
      if (!v) return;
      var val = v.slice(0, 64);
      // Normalize a rep slug through the alias map (e.g. igfam -> ig-fam, cam ->
      // cameron) so variants credit the same rep/pool. Unknown slugs pass through
      // untouched, so arbitrary ?rep= links still work.
      if (k === 'rep') {
        var canon = resolveRep(val);
        if (canon) val = canon;
      }
      fromUrl[k] = val;
      // Persistence is BEST-EFFORT and deliberately not depended on below.
      if (k === 'rep') storeRep(val);
      else storePartner(val); // session-scoped — see the note above storePartner
    });

    var attrs = {};
    KEYS.forEach(function (k) {
      // A value in the URL RIGHT NOW always wins, and is used directly rather
      // than read back out of storage.
      //
      // This used to round-trip through localStorage, so any browser that
      // refuses to write it — Safari with cross-site tracking prevention,
      // private browsing, an in-app Instagram/Facebook webview — silently
      // dropped the rep entirely: the setItem threw, the throw was swallowed,
      // `attrs` came out empty, and the cart was never stamped at all. The
      // customer clicked a valid rep link and the order recorded nothing.
      var v = fromUrl[k] || (k === 'rep' ? storedRep() : storedPartner());
      if (v) attrs[k] = v;
    });
    // Channel source is tied STRICTLY to the ig-fam pool code (Andrew's call),
    // so `source` never clutters other orders' reports: ig-fam == Instagram.
    if (attrs.rep === 'ig-fam') attrs.source = 'instagram';
    if (Object.keys(attrs).length) {
      var stamp = function () {
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var current = cart.attributes || {};
            var stale = Object.keys(attrs).some(function (k) { return current[k] !== attrs[k]; });
            if (!stale) return;
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes: attrs }),
            }).then(function () {
              // Only after the cart actually holds it — never show a credit the
              // cart does not have.
              syncFieldsTo(attrs.rep);
            });
          })
          .catch(function () { /* best-effort; never break the store */ });
      };
      stamp();
      window.addEventListener('pageshow', function (e) { if (e.persisted) stamp(); });
    }
  } catch (e) { /* storage unavailable — degrade silently */ }

  // The field must show the rep that will ACTUALLY be credited. It renders
  // server-side from cart.attributes.rep at page load, so after a ?rep= link
  // changes the cart it is showing the previous rep — and used to confirm
  // "✓ <old rep> will be credited" while the cart held someone else. Nothing
  // reassigned it, because the prefill only fires on an empty field.
  function syncFieldsTo(slug) {
    if (!slug) return;
    document.querySelectorAll('[data-fmr-referral]').forEach(function (box) {
      var input = box.querySelector('input');
      if (!input) return;
      input.value = slug;
      setStatus(box.querySelector('[data-fmr-status]'), resolveRep(slug), slug);
    });
  }

  // ---- 2) Manual referral field on the cart ----------------------------------
  function setStatus(el, slug, raw) {
    if (!el) return;
    el.className = 'fmr-referral__status';
    if (slug) {
      el.textContent = '✓ ' + (REP_NAMES[slug] || slug) + ' will be credited.';
      el.classList.add('is-ok');
    } else if (raw && raw.trim()) {
      el.textContent = 'Thanks! We’ll pass “' + raw.trim() + '” along to your rep.';
      el.classList.add('is-soft');
    } else {
      el.textContent = '';
    }
  }

  function initReferralFields() {
    var boxes = document.querySelectorAll('[data-fmr-referral]');
    boxes.forEach(function (box) {
      var input = box.querySelector('input');
      if (!input || input.dataset.fmrReady) return;
      input.dataset.fmrReady = '1';
      var status = box.querySelector('[data-fmr-status]');

      // Prefill from a rep captured via link so the customer sees the credit.
      if (!input.value) {
        var stored = storedRep();
        if (stored) input.value = stored;
      }
      if (input.value) setStatus(status, resolveRep(input.value), input.value);

      var apply = function () {
        var raw = input.value;
        var slug = resolveRep(raw);
        setStatus(status, slug, raw);
        if (slug) {
          input.value = slug; // submit the rep's slug, not free text
          storeRep(slug); // timestamped so it can expire (last-touch window)
          localStorage.removeItem('fmr-rep_note');
          writeRep(slug);
        } else if (raw && raw.trim()) {
          writeRepNote(raw.trim());
        }
      };
      input.addEventListener('change', apply);
      input.addEventListener('blur', apply);
    });
  }

  if (document.readyState !== 'loading') initReferralFields();
  else document.addEventListener('DOMContentLoaded', initReferralFields);
  // shopify:section:load fires in the THEME EDITOR only. It does not fire when a
  // customer changes a quantity, so on its own it never covered the case below.
  document.addEventListener('shopify:section:load', initReferralFields);

  // THE INCIDENT (2026-07-30): Dawn replaces the cart's innerHTML on every cart
  // mutation, which destroys this input along with its 'change'/'blur' listeners,
  // and Dawn publishes cart events through an in-module pubsub rather than on the
  // document. Nothing re-armed the field. So a customer who adjusted a quantity
  // and THEN typed a rep name got no confirmation and no `rep` attribute: the
  // credit was silently lost, with no error anywhere. Reproduced in a headless
  // harness before fixing.
  //
  // fmr-cart-mount.js owns the page's single MutationObserver and fires whenever
  // a [data-fmr-referral] anchor is re-added, which is exactly this case.
  // initReferralFields is idempotent (it guards on input.dataset.fmrReady), so
  // re-running it on every cart render is cheap and only arms genuinely new
  // inputs. Guarded because a partial theme push could ship this file without
  // the mount.
  if (window.FMR && window.FMR.onCartRender) {
    window.FMR.onCartRender(initReferralFields);
  }

  // ---- 3) Minimal-friction checkout nudge -----------------------------------
  // If a customer heads to checkout with NO rep set, gently prompt once: focus
  // the referral field and hint. A second tap proceeds (never blocks a sale).
  // Document-level + capture-phase so it survives cart re-renders and runs
  // before the form submits. Express-pay buttons aren't matched (can't be).
  var checkoutNudged = false;
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.cart__checkout-button, #checkout, #CartDrawer-Checkout');
    if (!btn || checkoutNudged) return;

    if (storedRep()) return; // rep already known (and not expired) -> proceed
    // offsetParent is null for the fixed-position drawer even when visible, so
    // test visibility via client rects instead.
    var vis = function (el) {
      if (!el || !el.getClientRects().length) return false;
      var s = getComputedStyle(el);
      return s.display !== 'none' && s.visibility !== 'hidden';
    };
    var typed = '';
    document.querySelectorAll('[data-fmr-referral] input').forEach(function (i) {
      if (vis(i) && i.value.trim()) typed = i.value.trim();
    });
    if (typed) return; // something entered -> proceed

    e.preventDefault();
    e.stopPropagation();
    checkoutNudged = true; // one-time only; the next tap goes through

    var box = null;
    document.querySelectorAll('[data-fmr-referral]').forEach(function (b) {
      if (vis(b)) box = b;
    });
    if (box) {
      var status = box.querySelector('[data-fmr-status]');
      if (status) {
        status.className = 'fmr-referral__status is-soft';
        status.textContent = 'Referred by a rep? Add their name above so they get credit, or tap Checkout again to continue.';
      }
      var input = box.querySelector('input');
      if (input) {
        try { input.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e2) {}
        input.focus();
      }
    }
  }, true);
})();
